// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNotify, usePermissions, useTranslate } from 'react-admin'

import { deadlineFetch } from '../hooks/customEndpointFetch'
import { CLINIC_PROFILE_PROMPT_DISMISS_KEY } from '../storeConfig'
import { authHeaders, errorMessageOf } from '../utils/api'
import {
  type BusinessAddress,
  type ClinicCountry,
  composeLegacyAddressLine,
  isValidClinicPhone,
  normalizeClinicCountry,
  resolveStateCode,
  stateOptionsFor,
} from '../utils/clinicAddress'
import { isValidPostalCode, normalizePostalCode } from '../utils/postalCode'
import {
  inferProfessionalRole,
  isAlreadySubmittedError,
  mapPracticeType,
  PRACTICE_TYPES,
  type PracticeType,
  type VerificationRequest,
} from './verificationDto'

/**
 * "Complete your clinic profile" — the step a vet who arrived WITHOUT a clinic
 * block is otherwise never offered.
 *
 * Why it exists (2026-09-11). A vet who signs in with Google/Microsoft is
 * minted by /api/users/oauth/login with an email and a name and nothing else:
 * no clinic name, no address, no phone, and `clinicVerificationStatus` left
 * at `unverified`. Nothing enqueues them for clinic verification, and — with
 * no password (passwordSet=false) — they cannot even reach the marketing
 * site's Shop Verification page, which is the only surface that both collects
 * the clinic block AND creates the review-queue row. My Profile (structured
 * since 5.1.87) lets them fill the block but does not enqueue either. So they
 * sit in `unverified` until someone notices (10 such accounts on 2026-09-11;
 * the same shape as the 233 "pending manual review that wrote nothing" vets
 * of REGISTRATION_FCA_UNIFICATION_PRD §1.4).
 *
 * What it does. For a signed-in MASTER vet (sub-accounts belong to their
 * master's clinic) whose status is `unverified`, it prefills whatever the
 * account already holds, asks for the rest, then:
 *   1. PATCHes the clinic block onto the account (`businessAddress` + the
 *      legacy one-line `address`, clinic_name, phone, professionalTitle,
 *      veterinarianCount) — best-effort, so the account stops being thin;
 *   2. POSTs the SAME request the site's verification page sends to
 *      /api/v1/shop/clinic-verification, which creates the queue row and
 *      moves the account to `pending` (the Go side patches Payload).
 * "Not now" dismisses it for this TAB (sessionStorage); a new tab or the next
 * session asks again, until the account leaves `unverified`.
 *
 * Reads the authProvider identity cache first (same as OnboardingSharePrompt)
 * and writes the new status back into it on success, so the prompt does not
 * re-show for the cache TTL after the vet has just submitted.
 */

// Re-exported for tests/importers — the key constant itself now lives in
// storeConfig.ts so clearUserScopedStoreKeys() (login+logout) can clear it.
export const DISMISS_KEY = CLINIC_PROFILE_PROMPT_DISMISS_KEY
const IDENTITY_CACHE_TTL = 5 * 60 * 1000

interface FormState {
  clinicName: string
  address1: string
  city: string
  state: string
  country: ClinicCountry
  zip: string
  phone: string
  professionalTitle: string
  practiceType: PracticeType | ''
  numberOfVets: string
  termsAccepted: boolean
}

const readCachedUser = (): any | null => {
  try {
    const raw = sessionStorage.getItem('identity_cache')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.data?.user || Date.now() - parsed.timestamp > IDENTITY_CACHE_TTL) return null
    return parsed.data.user
  } catch (_e) {
    return null
  }
}

const writeCachedUser = (patch: Record<string, unknown>) => {
  try {
    const raw = sessionStorage.getItem('identity_cache')
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (parsed?.data?.user) {
      parsed.data.user = { ...parsed.data.user, ...patch }
      sessionStorage.setItem('identity_cache', JSON.stringify(parsed))
    }
  } catch (_e) {
    // best-effort
  }
}

/** The gate: vet, master account, never submitted. Exported for the test. */
export const needsClinicProfile = (user: any): boolean => {
  if (!user || user.role !== 'vet') return false
  if (user.masterAccount) return false
  const status = user.clinicVerificationStatus
  return !status || status === 'unverified'
}

const formFromUser = (user: any): FormState => {
  const ba: Partial<BusinessAddress> = user.businessAddress ?? {}
  const country = normalizeClinicCountry(ba.country) ?? 'US'
  return {
    clinicName: user.hospitalName || user.clinic_name || '',
    address1: ba.address1 || user.address || '',
    city: ba.city || '',
    state: resolveStateCode(ba.state, country),
    country,
    zip: ba.postalCode || user.zip || '',
    phone: ba.phone || user.phone || '',
    professionalTitle: user.professionalTitle || '',
    practiceType: '',
    numberOfVets: user.veterinarianCount ? String(user.veterinarianCount) : '',
    termsAccepted: false,
  }
}

const CompleteClinicProfilePrompt = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const { permissions } = usePermissions()
  const isVet = permissions?.role === 'vet'

  const [user, setUser] = useState<any | null>(null)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<FormState | null>(null)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isVet) return
    if (sessionStorage.getItem(DISMISS_KEY)) return
    let cancelled = false
    const evaluate = (u: any) => {
      if (!needsClinicProfile(u)) return
      setUser(u)
      setForm(formFromUser(u))
      setOpen(true)
    }
    const cached = readCachedUser()
    if (cached) {
      evaluate(cached)
      return
    }
    ;(async () => {
      try {
        const res = await deadlineFetch('/api/users/me', { headers: authHeaders() })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled && data?.user) evaluate(data.user)
      } catch (_e) {
        // best-effort; never block the dashboard
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isVet])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))

  const handleCountryChange = (value: string) => {
    const country = normalizeClinicCountry(value) ?? 'US'
    setForm((prev) =>
      prev ? { ...prev, country, state: resolveStateCode(prev.state, country) } : prev,
    )
  }

  const handleSkip = () => {
    sessionStorage.setItem(DISMISS_KEY, '1')
    setOpen(false)
  }

  const validate = (f: FormState): string => {
    const required: Array<keyof FormState> = [
      'clinicName',
      'address1',
      'city',
      'state',
      'zip',
      'phone',
      'professionalTitle',
      'practiceType',
    ]
    if (required.some((k) => String(f[k] ?? '').trim() === '')) {
      return translate('pos.clinic_profile_prompt.validation_required')
    }
    if (!resolveStateCode(f.state, f.country)) {
      return translate('pos.profile.validation_state_invalid')
    }
    if (!isValidPostalCode(f.zip)) return translate('pos.profile.validation_postal_invalid')
    if (!isValidClinicPhone(f.phone)) return translate('pos.profile.validation_phone_invalid')
    if (!f.termsAccepted) return translate('pos.clinic_profile_prompt.validation_terms')
    return ''
  }

  const handleSubmit = async () => {
    if (!form || !user) return
    setError('')
    const problem = validate(form)
    if (problem) {
      setError(problem)
      return
    }
    setSubmitting(true)
    const state = resolveStateCode(form.state, form.country)
    const postalCode = normalizePostalCode(form.zip)
    const businessAddress: BusinessAddress = {
      ...(user.businessAddress ?? {}),
      address1: form.address1.trim(),
      city: form.city.trim(),
      state,
      postalCode,
      country: form.country,
    }
    const numberOfVets = Math.max(1, Number.parseInt(form.numberOfVets, 10) || 1)

    // 1. The account first, best-effort, so the profile is visible the moment
    //    the dialog closes. A failure must not stop the enqueue below: the
    //    queue row is the durable write, and ada-core copies its clinic block
    //    onto the account when it records the request
    //    (verification_service.go patchPayloadClinicProfile, eng-brain P66-6).
    //    Before that copy existed this PATCH was the ONLY writer of the block
    //    into Payload, so a transient failure here left a "pending" vet with a
    //    thin profile for good.
    try {
      await deadlineFetch(`/api/users/${user.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          clinic_name: form.clinicName.trim(),
          phone: form.phone.trim(),
          professionalTitle: form.professionalTitle.trim(),
          veterinarianCount: numberOfVets,
          businessAddress,
          address: composeLegacyAddressLine(businessAddress),
          zip: postalCode,
        }),
      })
    } catch (_e) {
      // best-effort
    }

    // 2. The queue row — the same request the site's verification page sends.
    const request: VerificationRequest = {
      professionalRole: inferProfessionalRole(form.professionalTitle),
      professionalTitle: form.professionalTitle.trim(),
      hospitalName: form.clinicName.trim(),
      hospitalType: mapPracticeType(form.practiceType),
      numberOfVets,
      businessAddress: {
        line1: businessAddress.address1 ?? '',
        line2: '',
        city: businessAddress.city ?? '',
        state,
        postalCode,
        country: form.country,
      },
      phoneNumber: form.phone.trim(),
    }
    try {
      const res = await deadlineFetch('/api/v1/shop/clinic-verification', {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(request),
      })
      if (res.ok) {
        const body = await res.json().catch(() => ({}))
        const status = body?.status || 'pending'
        writeCachedUser({ clinicVerificationStatus: status, businessAddress })
        notify(translate('pos.clinic_profile_prompt.success'), { type: 'success' })
        setOpen(false)
        return
      }
      const message = await errorMessageOf(res)
      if (isAlreadySubmittedError(message)) {
        // Already in the queue: the outcome this prompt exists for.
        writeCachedUser({ clinicVerificationStatus: 'pending' })
        setOpen(false)
        return
      }
      setError(message)
    } catch (_e) {
      setError(translate('pos.clinic_profile_prompt.error_generic'))
    } finally {
      setSubmitting(false)
    }
  }

  if (!open || !form) return null

  const stateOptions = stateOptionsFor(form.country)
  const t = (k: string) => translate(`pos.clinic_profile_prompt.${k}`)

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : handleSkip}
      maxWidth="sm"
      fullWidth
      data-testid="clinic-profile-prompt"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VerifiedUserIcon color="primary" />
        {t('title')}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('subtitle')}
        </Typography>
        <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
          <TextField
            fullWidth
            required
            label={translate('pos.profile.field_clinic_name')}
            value={form.clinicName}
            onChange={(e) => set('clinicName', e.target.value)}
            inputProps={{ 'data-testid': 'cpp-clinic-name' }}
          />
          <TextField
            fullWidth
            required
            label={translate('pos.profile.field_address')}
            value={form.address1}
            onChange={(e) => set('address1', e.target.value)}
            inputProps={{ 'data-testid': 'cpp-address1' }}
          />
          <TextField
            fullWidth
            required
            label={translate('pos.profile.field_city')}
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
            inputProps={{ 'data-testid': 'cpp-city' }}
          />
          <TextField
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            label={translate('pos.profile.field_country')}
            value={form.country}
            onChange={(e) => handleCountryChange(e.target.value)}
            inputProps={{ 'data-testid': 'cpp-country' }}
          >
            <option value="US">{translate('pos.profile.country_us')}</option>
            <option value="CA">{translate('pos.profile.country_ca')}</option>
          </TextField>
          <TextField
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            label={translate('pos.profile.field_state')}
            value={form.state}
            onChange={(e) => set('state', e.target.value)}
            inputProps={{ 'data-testid': 'cpp-state' }}
          >
            <option value="" />
            {stateOptions.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </TextField>
          <TextField
            fullWidth
            required
            label={translate('pos.profile.field_postal_code')}
            value={form.zip}
            onChange={(e) => set('zip', e.target.value)}
            inputProps={{ 'data-testid': 'cpp-zip' }}
          />
          <TextField
            fullWidth
            required
            label={translate('pos.profile.phone')}
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            inputProps={{ 'data-testid': 'cpp-phone' }}
          />
          <TextField
            fullWidth
            required
            label={t('field_professional_title')}
            placeholder={t('professional_title_placeholder')}
            value={form.professionalTitle}
            onChange={(e) => set('professionalTitle', e.target.value)}
            inputProps={{ 'data-testid': 'cpp-title' }}
          />
          <TextField
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            label={t('field_practice_type')}
            value={form.practiceType}
            onChange={(e) => set('practiceType', e.target.value as PracticeType | '')}
            inputProps={{ 'data-testid': 'cpp-practice-type' }}
          >
            <option value="" />
            {PRACTICE_TYPES.map((p) => (
              <option key={p} value={p}>
                {t(`practice_type_${p}`)}
              </option>
            ))}
          </TextField>
          <TextField
            fullWidth
            type="number"
            label={t('field_number_of_vets')}
            value={form.numberOfVets}
            onChange={(e) => set('numberOfVets', e.target.value)}
            inputProps={{ min: 1, max: 999, 'data-testid': 'cpp-number-of-vets' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.termsAccepted}
                onChange={(e) => set('termsAccepted', e.target.checked)}
                inputProps={{ 'data-testid': 'cpp-terms' } as any}
              />
            }
            label={t('terms')}
          />
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="cpp-error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSkip} disabled={submitting} data-testid="cpp-skip">
          {t('skip')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          data-testid="cpp-submit"
        >
          {submitting ? t('submitting') : t('submit')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompleteClinicProfilePrompt
